/* ==========================================================================
   SpotMyStuff — Hero / Purchase Panel (V3 — single-product design)
   Adds a second, merchant-picked product ("Add-on product" section
   setting) alongside the Tracking Card, via two independent paths:

   1. Add to Cart (RELIABLE) — listens for Horizon's own real
      "shopify:cart:lines-update" event (dispatched by product-form.js /
      @shopify/events as CartLinesUpdateEvent — confirmed by reading
      Horizon's actual source: https://cdn.shopify.com/storefront/standard-events.js
      defines its event name as literally "shopify:cart:lines-update").
      That event is dispatched on the <product-form-component> itself
      (bubbles: true) the INSTANT the native button is clicked — BEFORE
      the real /cart/add.js fetch even starts — carrying a deferred
      `event.promise` that only resolves once that fetch actually
      completes.

      IMPORTANT — this listener is registered ONCE at page load, not
      inside a button click handler. An earlier version of this file
      registered it inside the native button's own 'click' listener,
      which raced against Horizon's own click handler on the SAME
      button: whichever handler happens to be registered first (which
      this file does not control — Horizon's custom element can connect
      before or after this script runs) fires first and dispatches the
      event synchronously, so a listener added inside the LOSING click
      handler can miss an event that already finished bubbling. Listening
      persistently from page load sidesteps the race entirely — it's
      already there no matter which handler runs first. `event.target`
      containment (is the dispatching element inside THIS section's own
      CTA?) is used instead of a click flag to scope the listener to only
      this section's own add-to-cart form, so it ignores cart updates
      from anywhere else on the page (other sections, cart drawer
      quantity changes, etc). Never wraps, intercepts, or replaces
      Horizon's own <add-to-cart-component>.

   2. Buy Now / Shop Pay / Apple Pay / Google Pay (BEST-EFFORT, NOT
      GUARANTEED) — explicitly requested and accepted with this caveat.
      Accelerated-checkout buttons frequently navigate straight to a
      checkout session without ever emitting a cart event this file can
      observe, and Apple Pay / Google Pay's payment sheet must open
      SYNCHRONOUSLY within the same click as a browser security
      requirement — awaiting a fetch()'s response before letting that
      click proceed can silently stop the sheet from opening at all. So
      this path fires a fire-and-forget navigator.sendBeacon add-to-cart
      request in parallel with the click — it never calls
      preventDefault(), never awaits anything, and never delays the
      native handler. sendBeacon (not fetch) is used specifically because
      it's designed to keep sending even as the page navigates away
      immediately after — a plain fetch can get cancelled mid-flight in
      that scenario. This does NOT confirm success and the add-on may or
      may not land in that specific checkout session depending on timing
      and which payment method fires.

   V3 originally had no pack selector (single product, single variant).
   CHANGE (3-up bundle selector): Each merchant-assigned paid product
   renders its own native buy-buttons block, bound via closest.product.
   initCardSwitcher() toggles which panel is visible. Bundle 1 keeps
   native Add to Cart. Bundles 2 and 3 capture their form submits and
   add the paid cards plus separate £0 free products in one cart request.
   Buy 1's charger add-on is optional and follows the shopper's switch.
   Bundles 2 and 3 retain their existing separate free-charger additions.
   ========================================================================== */

(function () {
  'use strict';

  // Adds the item AND makes Horizon's own cart drawer / cart-icon badge
  // refresh themselves — confirmed by reading Horizon's actual source
  // (assets/component-cart-items.js, assets/cart-icon.js): both listen
  // for the SAME "shopify:cart:lines-update" event this file's reliable
  // path also listens for, and they redraw using `event.promise`'s
  // resolved `detail.sections` (freshly-rendered section HTML, keyed by
  // each <cart-items-component>'s own section id) and `detail.itemCount`
  // — NOT by independently noticing a plain /cart/add.js call happened.
  // A previous version of this function only did the plain add — which
  // genuinely succeeded server-side (confirmed via /cart.js showing both
  // line items and the Buy-X-Get-Y discount correctly applied) but the
  // drawer never learned about it and kept showing only the first item,
  // which is exactly why it looked broken even though checkout would
  // have been correct. This mirrors the same technique already proven
  // in assets/spotmystuff-hero-purchase-v2.js's dispatchCartLinesUpdate.
  function addToCart(items, onError) {
    var routeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var addUrl = routeRoot.replace(/\/?$/, '/') + 'cart/add.js';

    // Same section ids Horizon's own product-form-component requests on
    // every real add (see its connectedCallback: it collects every
    // <cart-items-component> on the page) — this is what makes the
    // response include ready-to-use HTML for the cart drawer's item list.
    var sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach(function (el) {
      if (el.dataset && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
    });

    var resolveFn;
    var promise = new Promise(function (resolve) { resolveFn = resolve; });
    var event = new CustomEvent('shopify:cart:lines-update', { bubbles: true, cancelable: false });
    event.action = 'add';
    event.context = 'product';
    event.lines = items.map(function (item) {
      return { merchandiseId: 'gid://shopify/ProductVariant/' + item.id, quantity: item.quantity };
    });
    event.promise = promise;
    document.dispatchEvent(event);

    return fetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        items: items,
        sections: sectionIds.join(','),
        sections_url: window.location.pathname
      })
    })
      .then(function (response) { return response.json(); })
      .then(function (result) {
        if (result && result.status) {
          throw new Error(result.message || 'Add to cart failed');
        }
        var routeCartUrl = routeRoot.replace(/\/?$/, '/') + 'cart.js';
        return fetch(routeCartUrl, { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.json(); })
          .catch(function () { return null; })
          .then(function (cart) {
            resolveFn({
              detail: {
                didError: false,
                itemCount: cart && typeof cart.item_count === 'number' ? cart.item_count : undefined,
                items: result.items || [result],
                source: 'spotmystuff-hero-purchase-v3',
                sourceId: '',
                sections: result.sections
              }
            });
            return true;
          });
      })
      .catch(function (err) {
        resolveFn({ detail: { didError: true } });
        if (onError) onError(err);
        if (window.console && console.error) {
          console.error('[spotmystuff-hero-purchase-v3] Cart add failed:', err);
        }
        return false;
      });
  }

  // Best-effort only — see file header. Never awaited, never blocks the
  // native accelerated-checkout click that triggered it.
  function addViaBeaconBestEffort(items) {
    if (!items.length) return;
    var routeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var addUrl = routeRoot.replace(/\/?$/, '/') + 'cart/add.js';
    var payload = JSON.stringify({ items: items });

    if (navigator.sendBeacon) {
      try {
        var ok = navigator.sendBeacon(addUrl, new Blob([payload], { type: 'application/json' }));
        if (ok) return;
      } catch (e) {}
    }
    // Fallback for browsers without sendBeacon (or if it refused to queue
    // the request): fire-and-forget fetch with keepalive, which survives
    // a same-tab navigation for a short window in most browsers — best
    // effort here too, not a guarantee.
    try {
      fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true
      }).catch(function () {});
    } catch (e) {}
  }

  // Bundle 2 adds its paid cards, free card, charger and optional gift. Horizon's
  // submit handler runs on document in the capture phase, before a listener
  // on this panel can stop it. Remove only this panel's native submit action
  // so it cannot also add one extra paid card.
  function initBundle2AddToCart(cta, freeVariantId, freeChargerVariantId, secondFreeVariantId, mysteryGiftVariantId) {
    if (cta._smst3Bundle2AddReady) return;
    var productForm = cta.querySelector('product-form-component');
    if (!productForm || !productForm.querySelector('form[data-type="add-to-cart-form"]')) return;
    cta._smst3Bundle2AddReady = true;
    function suppressNativeSubmit() {
      cta.querySelectorAll('product-form-component').forEach(function (component) {
        if (component.hasAttribute('on:submit')) component.removeAttribute('on:submit');
      });
    }
    suppressNativeSubmit();
    new MutationObserver(suppressNativeSubmit).observe(cta, { childList: true, subtree: true, attributes: true, attributeFilter: ['on:submit'] });
    var pending = false;

    function showError(error) {
      var errorElement = cta.querySelector('.product-form-text__error');
      if (!errorElement) return;
      var message = error && error.message ? error.message : 'Unable to add Bundle 2 to the cart.';
      errorElement.textContent = message;
      errorElement.setAttribute('role', 'alert');
      errorElement.classList.remove('hidden');
    }

    cta.addEventListener('submit', function (event) {
      var form = cta.querySelector('product-form-component form[data-type="add-to-cart-form"]');
      if (!form || event.target !== form) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (pending) return;
      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }

      // Read the live form value so a variant change is respected. The free
      // variant comes from the separate product picker in this section.
      var variantInput = form.querySelector('input[name="id"]');
      var paidVariantId = variantInput && variantInput.value;
      if (!paidVariantId || !freeVariantId || !freeChargerVariantId || paidVariantId === freeVariantId || paidVariantId === freeChargerVariantId || freeVariantId === freeChargerVariantId) {
        showError(new Error('Bundle 2 products are not configured correctly.'));
        return;
      }

      var errorElement = form.querySelector('.product-form-text__error');
      if (errorElement) errorElement.classList.add('hidden');
      // A unique, private property keeps this 2 + 1 + 1 offer separate from
      // Bundle 1 and from another Bundle 2 purchase of the same variant.
      var offerId = window.crypto && window.crypto.randomUUID ? window.crypto.randomUUID() : Date.now().toString(36) + Math.random().toString(36).slice(2);
      var paidProperties = { _bundle_offer: 'bundle_2', _bundle_instance: offerId, _bundle_free_variant_1: String(freeVariantId), _bundle_free_charger_variant: String(freeChargerVariantId) };
      if (secondFreeVariantId) paidProperties._bundle_free_variant_2 = String(secondFreeVariantId);
      if (mysteryGiftVariantId) paidProperties._bundle_free_mystery_variant = String(mysteryGiftVariantId);
      var items = [
        { id: paidVariantId, quantity: 2, properties: paidProperties },
        { id: freeVariantId, quantity: 1, parent_id: Number(paidVariantId), properties: { _bundle_offer: 'bundle_2', _bundle_free: 'true', _bundle_instance: offerId } },
        { id: freeChargerVariantId, quantity: 1, parent_id: Number(paidVariantId), properties: { _bundle_offer: 'bundle_2', _bundle_free: 'true', _bundle_free_type: 'charger', _bundle_instance: offerId } }
      ];
      pending = true;
      var routeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      routeRoot = routeRoot.replace(/\/?$/, '/');
      var giftCheck = mysteryGiftVariantId
        ? fetch(routeRoot + 'cart.js', { headers: { Accept: 'application/json' } })
          .then(function (response) { if (!response.ok) throw new Error('Unable to read the cart.'); return response.json(); })
          .then(function (cart) {
            return !(cart.items || []).some(function (item) {
              return String(item.variant_id) === String(mysteryGiftVariantId) && (item.properties || {})._bundle_free_type === 'mystery_gift';
            });
          })
          .catch(function (error) {
            if (window.console && console.warn) console.warn('[spotmystuff-hero-purchase-v3] Mystery Gift cart check failed:', error);
            return false;
          })
        : Promise.resolve(false);
      giftCheck.then(function (shouldAddGift) {
        if (shouldAddGift) items.push({
          id: Number(mysteryGiftVariantId), quantity: 1, parent_id: Number(paidVariantId),
          properties: { _bundle_offer: 'bundle_2', _bundle_free: 'true', _bundle_free_type: 'mystery_gift', _bundle_instance: offerId }
        });
        return addToCart(items, shouldAddGift ? null : showError).then(function (added) {
          if (added || !shouldAddGift) return;
          // The optional gift must never prevent the paid bundle from being added.
          return addToCart(items.slice(0, -1), showError);
        });
      }).catch(showError).finally(function () { pending = false; });
    }, true);
  }

  // Bundle 3 uses the same single-request pattern as Bundle 2, with three
  // paid cards and two separate quantity-1 free-card lines. Its own handler
  // leaves Bundle 2 intact.
  function initBundle3AddToCart(cta, freeVariantId, secondFreeVariantId, freeChargerVariantId, mysteryGiftVariantId) {
    if (cta._smst3Bundle3AddReady) return;
    var productForm = cta.querySelector('product-form-component');
    if (!productForm || !productForm.querySelector('form[data-type="add-to-cart-form"]')) return;
    cta._smst3Bundle3AddReady = true;
    function suppressNativeSubmit() {
      cta.querySelectorAll('product-form-component').forEach(function (component) {
        if (component.hasAttribute('on:submit')) component.removeAttribute('on:submit');
      });
    }
    suppressNativeSubmit();
    new MutationObserver(suppressNativeSubmit).observe(cta, { childList: true, subtree: true, attributes: true, attributeFilter: ['on:submit'] });
    var pending = false;

    function showError(error) {
      var errorElement = cta.querySelector('.product-form-text__error');
      if (!errorElement) return;
      var message = error && error.message ? error.message : 'Unable to add Bundle 3 to the cart.';
      errorElement.textContent = message;
      errorElement.setAttribute('role', 'alert');
      errorElement.classList.remove('hidden');
    }

    cta.addEventListener('submit', function (event) {
      var form = cta.querySelector('product-form-component form[data-type="add-to-cart-form"]');
      if (!form || event.target !== form) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (pending) return;
      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }

      var variantInput = form.querySelector('input[name="id"]');
      var paidVariantId = variantInput && variantInput.value;
      if (!paidVariantId || !freeVariantId || !secondFreeVariantId || !freeChargerVariantId || paidVariantId === freeVariantId || paidVariantId === secondFreeVariantId || paidVariantId === freeChargerVariantId || freeVariantId === freeChargerVariantId || secondFreeVariantId === freeChargerVariantId) {
        showError(new Error('Bundle 3 products are not configured correctly.'));
        return;
      }

      var errorElement = form.querySelector('.product-form-text__error');
      if (errorElement) errorElement.classList.add('hidden');
      var offerId = window.crypto && window.crypto.randomUUID ? window.crypto.randomUUID() : Date.now().toString(36) + Math.random().toString(36).slice(2);
      var paidProperties = { _bundle_offer: 'bundle_3', _bundle_instance: offerId, _bundle_free_variant_1: String(freeVariantId), _bundle_free_variant_2: String(secondFreeVariantId), _bundle_free_charger_variant: String(freeChargerVariantId) };
      if (mysteryGiftVariantId) paidProperties._bundle_free_mystery_variant = String(mysteryGiftVariantId);
      var items = [
        { id: paidVariantId, quantity: 3, properties: paidProperties },
        { id: freeVariantId, quantity: 1, parent_id: Number(paidVariantId), properties: { _bundle_offer: 'bundle_3', _bundle_free: 'true', _bundle_free_slot: '1', _bundle_instance: offerId } },
        { id: secondFreeVariantId, quantity: 1, parent_id: Number(paidVariantId), properties: { _bundle_offer: 'bundle_3', _bundle_free: 'true', _bundle_free_slot: '2', _bundle_instance: offerId } },
        { id: freeChargerVariantId, quantity: 1, parent_id: Number(paidVariantId), properties: { _bundle_offer: 'bundle_3', _bundle_free: 'true', _bundle_free_type: 'charger', _bundle_instance: offerId } }
      ];
      pending = true;
      var routeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      routeRoot = routeRoot.replace(/\/?$/, '/');
      var giftCheck = mysteryGiftVariantId
        ? fetch(routeRoot + 'cart.js', { headers: { Accept: 'application/json' } })
          .then(function (response) { if (!response.ok) throw new Error('Unable to read the cart.'); return response.json(); })
          .then(function (cart) {
            return !(cart.items || []).some(function (item) {
              return String(item.variant_id) === String(mysteryGiftVariantId) && (item.properties || {})._bundle_free_type === 'mystery_gift';
            });
          })
          .catch(function (error) {
            if (window.console && console.warn) console.warn('[spotmystuff-hero-purchase-v3] Mystery Gift cart check failed:', error);
            return false;
          })
        : Promise.resolve(false);
      giftCheck.then(function (shouldAddGift) {
        if (shouldAddGift) items.push({
          id: Number(mysteryGiftVariantId), quantity: 1, parent_id: Number(paidVariantId),
          properties: { _bundle_offer: 'bundle_3', _bundle_free: 'true', _bundle_free_type: 'mystery_gift', _bundle_instance: offerId }
        });
        return addToCart(items, shouldAddGift ? null : showError).then(function (added) {
          if (added || !shouldAddGift) return;
          // The optional gift must never prevent the paid bundle from being added.
          return addToCart(items.slice(0, -1), showError);
        });
      }).catch(showError).finally(function () { pending = false; });
    }, true);
  }

  function bundle1CartIdentity(root) {
    var two = root.querySelector('.spotmystuff-hero-purchase-v3__cta--2');
    var three = root.querySelector('.spotmystuff-hero-purchase-v3__cta--3');
    var firstCard = two && two.getAttribute('data-bundle2-free-variant-id');
    var charger = two && two.getAttribute('data-bundle2-free-charger-variant-id');
    if (!firstCard || !charger) return null;
    // Buy 1 is the quantity-one state of the existing quantity-based offer.
    // Keep its established property contract so the working cart engine,
    // nested gift rows and saved-cart behavior need no separate bundle path.
    var properties = {
      _bundle_offer: 'bundle_2',
      _bundle_origin: 'bundle_1',
      _bundle_instance: 'smst3-b1-' + Date.now() + '-' + Math.random().toString(36).slice(2),
      _bundle_free_variant_1: String(firstCard),
      _bundle_free_charger_variant: String(charger)
    };
    var secondCard = three && three.getAttribute('data-bundle3-free-variant-id-2');
    var mystery = (two && two.getAttribute('data-bundle2-mystery-gift-variant-id')) || (three && three.getAttribute('data-bundle3-mystery-gift-variant-id'));
    if (secondCard) properties._bundle_free_variant_2 = String(secondCard);
    if (mystery) properties._bundle_free_mystery_variant = String(mystery);
    return properties;
  }

  function initBundle1CartIdentity(cta, root) {
    if (cta._smst3Bundle1IdentityReady) return;
    cta._smst3Bundle1IdentityReady = true;
    function prepare(form) {
      if (!form || !form.matches('form[data-type="add-to-cart-form"]') || !cta.contains(form)) return;
      var properties = bundle1CartIdentity(root);
      if (!properties) return;
      Object.keys(properties).forEach(function (key) {
        var name = 'properties[' + key + ']';
        var input = form.querySelector('input[name="' + name + '"]');
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          form.appendChild(input);
        }
        input.value = properties[key];
      });
    }
    prepare(cta.querySelector('form[data-type="add-to-cart-form"]'));
    // Capture runs before Horizon serializes the native form. Each separate
    // purchase gets its own instance; cart quantity changes retain that ID.
    cta.addEventListener('submit', function (event) { prepare(event.target); }, true);
  }

  function initCta(root) {
    // Change: 3-up bundle selector — wire up EVERY cta panel (one per
    // bundle's own buy-buttons block), not just the first. Each bundle
    // is a genuinely separate product. Bundles 2 and 3 use their own
    // selected £0 chargers. The optional paid charger is scoped to
    // Bundle 1 and requires the shopper's switch to be enabled.
    var ctas = root.querySelectorAll('[data-spotmystuff-v3-cta]');
    ctas.forEach(function (cta) {
      if (cta.classList.contains('spotmystuff-hero-purchase-v3__cta--1')) initBundle1CartIdentity(cta, root);
      var addonVariantId = cta.getAttribute('data-charger-variant-id');
      var addonQty = Number(cta.getAttribute('data-charger-quantity'));
      var freeVariantId = cta.getAttribute('data-bundle2-free-variant-id');
      var freeChargerVariantId = cta.getAttribute('data-bundle2-free-charger-variant-id');
      var bundle3FreeVariantId = cta.getAttribute('data-bundle3-free-variant-id');
      var bundle3SecondFreeVariantId = cta.getAttribute('data-bundle3-free-variant-id-2');
      var bundle3FreeChargerVariantId = cta.getAttribute('data-bundle3-free-charger-variant-id');
      var bundle3MysteryGiftVariantId = cta.getAttribute('data-bundle3-mystery-gift-variant-id');
      var addonItems = addonVariantId && addonQty ? [{ id: Number(addonVariantId), quantity: addonQty }] : [];
      if (freeVariantId) initBundle2AddToCart(cta, freeVariantId, freeChargerVariantId, root.querySelector('.spotmystuff-hero-purchase-v3__cta--3')?.getAttribute('data-bundle3-free-variant-id-2'), cta.getAttribute('data-bundle2-mystery-gift-variant-id'));
      if (bundle3FreeVariantId) initBundle3AddToCart(cta, bundle3FreeVariantId, bundle3SecondFreeVariantId, bundle3FreeChargerVariantId, bundle3MysteryGiftVariantId);
      if (!addonVariantId || !addonQty || !cta.classList.contains('spotmystuff-hero-purchase-v3__cta--1')) return;

      function selectedAddonItems() {
        var toggle = root.querySelector('[data-smst3-charger-toggle]');
        return toggle && toggle.checked && !toggle.disabled ? addonItems : [];
      }

      // Registered once per panel, at init — see file header for why
      // this can't live inside a button click handler. Persists for the
      // page's lifetime; every real cart-lines-update anywhere on the
      // page is inspected and ignored unless it came from THIS panel's
      // own add-to-cart form.
      document.addEventListener('shopify:cart:lines-update', function (event) {
        if (event.action !== 'add') return;
        if (!event.target || !cta.contains(event.target)) return;
        if (!event.promise) return;
        var items = selectedAddonItems();
        if (!items.length) return;
        event.promise.then(function (payload) {
          if (payload && payload.detail && payload.detail.didError) return;
          addToCart(items);
        }).catch(function () {});
      });

      // Accelerated checkout (Buy Now / Shop Pay / Apple Pay / Google Pay).
      // Delegated + capture so this still fires even if Shopify's own
      // script re-renders the button markup inside the block, or if the
      // actual clickable element lives in a shadow root (composed click
      // events still bubble/capture through shadow boundaries).
      var acceleratedBlock = cta.querySelector('.accelerated-checkout-block');
      if (acceleratedBlock) {
        acceleratedBlock.addEventListener('click', function () {
          addViaBeaconBestEffort(selectedAddonItems());
        }, true);
      }
    });
  }

  function addCartOfferTag(row, className, label) {
    if (!row || row.querySelector('.' + className)) return;
    var tagParent = row.querySelector('.cart-items__unit-price-wrapper') || row.querySelector('.cart-items__product-info');
    if (!tagParent) return;
    var tag = document.createElement('span');
    tag.className = className;
    tag.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 11V5a2 2 0 0 1 2-2h6l10 10-8 8L3 11Z"/><circle cx="8" cy="8" r="1"/></svg>';
    var text = document.createElement('span');
    text.textContent = label;
    tag.appendChild(text);
    tagParent.appendChild(tag);
  }

  // Horizon changes the paid cart line first. Once that update succeeds,
  // reconcile the free lines belonging to the same private bundle instance.
  // A second free card is attached to an existing parent by line key, not by
  // variant id (which only works when parent and child are added together).
  function initBundleCartQuantitySync() {
    if (window.__smst3BundleCartQuantitySyncReady) return;
    window.__smst3BundleCartQuantitySyncReady = true;
    var routeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    routeRoot = routeRoot.replace(/\/?$/, '/');
    var queue = Promise.resolve();
    var cartItems = null;
    var cartRevision = 0;
    var giftAddBlocked = false;
    var bundle1LegacyInstances = Object.create(null);

    function getCart() {
      return fetch(routeRoot + 'cart.js', { headers: { Accept: 'application/json' } })
        .then(function (response) {
          if (!response.ok) throw new Error('Unable to read the cart.');
          return response.json();
        });
    }

    // Keep the server's row/index order intact for Horizon's quantity controls.
    // Only the visual order changes when Shopify appends a restored free card
    // after its sibling charger.
    function showBundleRowsInOrder() {
      if (!cartItems) return;
      var groups = Object.create(null);
      cartItems.forEach(function (item) {
        var properties = item.properties || {};
        if (!properties._bundle_instance) return;
        (groups[properties._bundle_instance] || (groups[properties._bundle_instance] = [])).push(item);
      });
      var ordered = [];
      var seen = Object.create(null);
      cartItems.forEach(function (item) {
        var instance = (item.properties || {})._bundle_instance;
        if (!instance) { ordered.push(item); return; }
        if (seen[instance]) return;
        seen[instance] = true;
        groups[instance].sort(function (a, b) {
          function rank(line) {
            var properties = line.properties || {};
            if (properties._bundle_free !== 'true') return 0;
            if (properties._bundle_free_type === 'mystery_gift') return 4;
            if (properties._bundle_free_type === 'charger') return 3;
            return properties._bundle_free_slot === '2' ? 2 : 1;
          }
          return rank(a) - rank(b);
        });
        ordered.push.apply(ordered, groups[instance]);
      });
      var positions = Object.create(null);
      ordered.forEach(function (item, index) { positions[item.key] = index; });
      var itemByKey = Object.create(null);
      cartItems.forEach(function (item) { itemByKey[item.key] = item; });
      document.querySelectorAll('cart-items-component .cart-items__table tbody').forEach(function (body) {
        var rows = Array.from(body.querySelectorAll(':scope > .cart-items__table-row'));
        var needsOrder = rows.some(function (row, index) {
          return positions[row.dataset.key] !== undefined && positions[row.dataset.key] !== index;
        });
        body.classList.toggle('smst3-bundle-cart-order', needsOrder);
        rows.forEach(function (row) {
          row.style.order = needsOrder && positions[row.dataset.key] !== undefined ? positions[row.dataset.key] : '';
          var item = itemByKey[row.dataset.key];
          var properties = item && item.properties || {};
          if (properties._bundle_free !== 'true' || properties._bundle_free_type !== 'mystery_gift' || !item || Number(item.final_line_price) !== 0) return;
          addCartOfferTag(row, 'smst3-cart-free-gift-tag', 'FREE GIFT');
          if (row.classList.contains('smst3-cart-mystery-gift')) return;
          row.classList.add('smst3-cart-mystery-gift');
          row.querySelectorAll('a.cart-items__media-container, a.cart-items__title').forEach(function (link) {
            var label = document.createElement('span');
            label.className = link.className;
            if (link.hasAttribute('style')) label.setAttribute('style', link.getAttribute('style'));
            while (link.firstChild) label.appendChild(link.firstChild);
            link.replaceWith(label);
          });
          var total = row.querySelector('.cart-items__price text-component');
          if (total) { total.setAttribute('value', 'FREE'); total.textContent = 'FREE'; }
          var unit = row.querySelector('.cart-items__unit-price-wrapper > div > span bdi');
          if (unit) unit.textContent = 'FREE';
          var quantity = row.querySelector('cart-quantity-selector-component input[name="updates[]"]');
          if (quantity) { quantity.value = '1'; quantity.min = '1'; quantity.max = '1'; quantity.readOnly = true; quantity.setAttribute('aria-readonly', 'true'); }
        });
      });
    }

    document.addEventListener('click', function (event) {
      var link = event.target && event.target.closest && event.target.closest('.smst3-cart-mystery-gift a.cart-items__media-container, .smst3-cart-mystery-gift a.cart-items__title');
      if (link) { event.preventDefault(); event.stopPropagation(); }
    }, true);

    function rememberCart(cart) {
      if (!cart || !Array.isArray(cart.items)) return;
      cartItems = cart.items;
      cartRevision++;
      showBundleRowsInOrder();
    }

    // A section morph replaces rows after its event promise resolves.
    new MutationObserver(showBundleRowsInOrder).observe(document.body, { childList: true, subtree: true });
    var initialRevision = cartRevision;
    Promise.resolve().then(getCart).then(function (cart) {
      if (cartRevision === initialRevision) rememberCart(cart);
    }).catch(function () {});

    function waitForNativeCartRender(component) {
      if (!component.classList || !component.classList.contains('cart-items-disabled')) return Promise.resolve();
      return new Promise(function (resolve) {
        var observer = new MutationObserver(function () {
          if (!component.classList.contains('cart-items-disabled')) finish();
        });
        var timeout = setTimeout(finish, 2000);
        function finish() {
          clearTimeout(timeout);
          observer.disconnect();
          resolve();
        }
        observer.observe(component, { attributes: true, attributeFilter: ['class'] });
        if (!component.classList.contains('cart-items-disabled')) finish();
      });
    }

    function changeFreeLine(key, quantity, renderReady, properties) {
      var sectionIds = [];
      document.querySelectorAll('cart-items-component').forEach(function (el) {
        if (el.dataset && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
      });
      var resolveEvent;
      var eventPromise = new Promise(function (resolve) { resolveEvent = resolve; });
      var event = new CustomEvent('shopify:cart:lines-update', { bubbles: true });
      event.action = quantity === 0 ? 'remove' : 'update';
      event.context = 'cart';
      event.lines = [{ id: key, quantity: quantity }];
      event.promise = eventPromise;
      document.dispatchEvent(event);

      return fetch(routeRoot + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: key,
          quantity: quantity,
          properties: properties,
          sections: sectionIds.join(','),
          sections_url: window.location.pathname
        })
      })
        .then(function (response) {
          return response.json().then(function (cart) {
            if (!response.ok || cart.status || cart.errors) throw new Error(cart.description || cart.message || 'Unable to update the free card.');
            return Promise.resolve(renderReady).then(function () {
              resolveEvent({ detail: { didError: false, itemCount: cart.item_count, items: cart.items, sections: cart.sections } });
              return cart;
            });
          });
        })
        .catch(function (error) {
          resolveEvent({ detail: { didError: true } });
          throw error;
        });
    }

    function updateFreeLines(updates, renderReady) {
      var sectionIds = [];
      document.querySelectorAll('cart-items-component').forEach(function (el) {
        if (el.dataset && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
      });
      var resolveEvent;
      var eventPromise = new Promise(function (resolve) { resolveEvent = resolve; });
      var event = new CustomEvent('shopify:cart:lines-update', { bubbles: true });
      event.action = Object.keys(updates).some(function (key) { return updates[key] === 0; }) ? 'remove' : 'update';
      event.context = 'cart';
      event.lines = Object.keys(updates).map(function (key) { return { id: key, quantity: updates[key] }; });
      event.promise = eventPromise;
      document.dispatchEvent(event);

      return fetch(routeRoot + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates: updates, sections: sectionIds.join(','), sections_url: window.location.pathname })
      }).then(function (response) {
        return response.json().then(function (cart) {
          if (!response.ok || cart.status || cart.errors) throw new Error(cart.description || cart.message || 'Unable to update the bundle rewards.');
          return Promise.resolve(renderReady).then(function () {
            resolveEvent({ detail: { didError: false, itemCount: cart.item_count, items: cart.items, sections: cart.sections } });
            return cart;
          });
        });
      }).catch(function (error) {
        resolveEvent({ detail: { didError: true } });
        throw error;
      });
    }

    function addFreeLines(items, cart, renderReady, eventContext) {
      var sectionIds = [];
      document.querySelectorAll('cart-items-component').forEach(function (el) {
        if (el.dataset && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
      });
      var resolveEvent;
      var eventPromise = new Promise(function (resolve) { resolveEvent = resolve; });
      var event = new CustomEvent('shopify:cart:lines-update', { bubbles: true });
      event.action = 'add';
      event.context = eventContext || 'product';
      event.lines = items.map(function (item) { return { merchandiseId: 'gid://shopify/ProductVariant/' + item.id, quantity: item.quantity }; });
      event.promise = eventPromise;
      document.dispatchEvent(event);

      function post(batch) {
        return fetch(routeRoot + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: batch, sections: sectionIds.join(','), sections_url: window.location.pathname })
        }).then(function (response) {
          return response.json().then(function (result) {
            if (!response.ok || result.status || result.errors) throw new Error(result.description || result.message || 'Unable to add the bundle reward.');
            return result;
          });
        });
      }
      var hasGift = items.some(function (item) { return (item.properties || {})._bundle_free_type === 'mystery_gift'; });
      return post(items).catch(function (error) {
        if (!hasGift) throw error;
        giftAddBlocked = true;
        if (window.console && console.warn) console.warn('[spotmystuff-hero-purchase-v3] Mystery Gift could not be added; continuing without it:', error);
        var requiredItems = items.filter(function (item) { return (item.properties || {})._bundle_free_type !== 'mystery_gift'; });
        return requiredItems.length ? post(requiredItems) : null;
      }).then(function (result) {
        if (!result) {
          resolveEvent({ detail: { didError: true } });
          return [];
        }
        var added = result.items || [result];
        var count = typeof cart.item_count === 'number' ? cart.item_count : cart.items.reduce(function (sum, line) { return sum + line.quantity; }, 0);
        var addedCount = added.reduce(function (sum, item) { return sum + item.quantity; }, 0);
        return Promise.resolve(renderReady).then(function () {
          resolveEvent({ detail: { didError: false, itemCount: count + addedCount, items: added, sections: result.sections } });
          return added;
        });
      }).catch(function (error) {
        resolveEvent({ detail: { didError: true } });
        throw error;
      });
    }

    function configuredFreeVariant(paid, slot) {
      var properties = paid.properties || {};
      var saved = properties['_bundle_free_variant_' + slot];
      if (saved) return saved;
      var selector = slot === 2
        ? '.spotmystuff-hero-purchase-v3__cta--3[data-bundle3-free-variant-id-2]'
        : (properties._bundle_offer === 'bundle_3'
          ? '.spotmystuff-hero-purchase-v3__cta--3[data-bundle3-free-variant-id]'
          : '.spotmystuff-hero-purchase-v3__cta--2[data-bundle2-free-variant-id]');
      var cta = document.querySelector(selector);
      return cta && cta.getAttribute(slot === 2 ? 'data-bundle3-free-variant-id-2' : properties._bundle_offer === 'bundle_3' ? 'data-bundle3-free-variant-id' : 'data-bundle2-free-variant-id');
    }

    function configuredChargerVariant(paid) {
      var properties = paid.properties || {};
      if (properties._bundle_free_charger_variant) return properties._bundle_free_charger_variant;
      var isThree = properties._bundle_offer === 'bundle_3';
      var attribute = isThree ? 'data-bundle3-free-charger-variant-id' : 'data-bundle2-free-charger-variant-id';
      var cta = document.querySelector('.spotmystuff-hero-purchase-v3__cta--' + (isThree ? '3' : '2') + '[' + attribute + ']');
      return cta && cta.getAttribute(attribute);
    }

    function configuredMysteryVariant(paid) {
      var saved = (paid.properties || {})._bundle_free_mystery_variant;
      if (saved) return saved;
      var two = document.querySelector('.spotmystuff-hero-purchase-v3__cta--2[data-bundle2-mystery-gift-variant-id]');
      if (two) return two.getAttribute('data-bundle2-mystery-gift-variant-id');
      var cta = document.querySelector('.spotmystuff-hero-purchase-v3__cta--3[data-bundle3-mystery-gift-variant-id]');
      return cta && cta.getAttribute('data-bundle3-mystery-gift-variant-id');
    }

    function nextRewardAction(cart) {
      var groups = Object.create(null);
      var giftLines = [];
      (cart.items || []).forEach(function (item) {
        var properties = item.properties || {};
        if (properties._bundle_free === 'true' && properties._bundle_free_type === 'mystery_gift') {
          giftLines.push(item);
          return;
        }
        var instance = properties._bundle_instance;
        if (!instance) return;
        var group = groups[instance] || (groups[instance] = { instance: instance, paid: null, cards: [], chargers: [] });
        if (properties._bundle_free === 'true') {
          if (properties._bundle_free_type === 'charger') group.chargers.push(item);
          else group.cards.push(item);
        } else if (properties._bundle_offer === 'bundle_2' || properties._bundle_offer === 'bundle_3') {
          group.paid = item;
        }
      });

      var instances = Object.keys(groups);
      var giftParent = null;
      var giftVariantId = null;
      instances.some(function (instance) {
        var paid = groups[instance].paid;
        if (!paid || paid.quantity < 2) return false;
        var variantId = configuredMysteryVariant(paid);
        if (!variantId) return false;
        giftParent = paid;
        giftVariantId = String(variantId);
        return true;
      });
      var keptGift = null;
      var giftUpdates = Object.create(null);
      giftLines.forEach(function (item) {
        var instance = (item.properties || {})._bundle_instance;
        var parent = groups[instance] && groups[instance].paid;
        var valid = giftParent && parent && parent.quantity >= 2 && String(item.variant_id) === giftVariantId;
        if (valid && !keptGift) {
          keptGift = item;
          if (item.quantity !== 1) giftUpdates[item.key] = 1;
        } else giftUpdates[item.key] = 0;
      });
      var giftToAdd = giftParent && !keptGift && !giftAddBlocked ? {
        id: Number(giftVariantId), quantity: 1, parent_line_key: giftParent.key,
        properties: { _bundle_offer: (giftParent.properties || {})._bundle_offer, _bundle_free: 'true', _bundle_free_type: 'mystery_gift', _bundle_instance: (giftParent.properties || {})._bundle_instance }
      } : null;
      function withGift(action) {
        var giftKeys = Object.keys(giftUpdates);
        if (action && (action.type === 'change' || action.type === 'update') && giftKeys.length) {
          var updates = Object.assign(Object.create(null), giftUpdates);
          if (action.type === 'change') updates[action.key] = action.quantity;
          else Object.assign(updates, action.updates);
          return { type: 'update', updates: updates };
        }
        if (action && action.type === 'add' && giftToAdd && !giftKeys.length) {
          action.items.push(giftToAdd);
          return action;
        }
        if (action) return action;
        if (giftKeys.length === 1) return { type: 'change', key: giftKeys[0], quantity: giftUpdates[giftKeys[0]] };
        if (giftKeys.length) return { type: 'update', updates: giftUpdates };
        if (giftToAdd) return { type: 'add', items: [giftToAdd] };
        return null;
      }
      for (var i = 0; i < instances.length; i++) {
        var group = groups[instances[i]];
        var paid = group.paid;
        var targetCards = paid ? (paid.quantity >= 3 ? 2 : paid.quantity === 2 ? 1 : 0) : 0;
        var targetCharger = paid && paid.quantity >= 2 ? 1 : 0;
        var freeCount = group.cards.reduce(function (sum, item) { return sum + item.quantity; }, 0);
        var chargerCount = group.chargers.reduce(function (sum, item) { return sum + item.quantity; }, 0);
        var updates = Object.create(null);
        var cardsToRemove = Math.max(0, freeCount - targetCards);
        group.cards.slice().sort(function (a, b) {
          return ((b.properties || {})._bundle_free_slot === '2') - ((a.properties || {})._bundle_free_slot === '2');
        }).forEach(function (item) {
          if (!cardsToRemove) return;
          var remove = Math.min(cardsToRemove, item.quantity);
          updates[item.key] = item.quantity - remove;
          cardsToRemove -= remove;
        });
        var chargersToRemove = Math.max(0, chargerCount - targetCharger);
        group.chargers.forEach(function (item) {
          if (!chargersToRemove) return;
          var remove = Math.min(chargersToRemove, item.quantity);
          updates[item.key] = item.quantity - remove;
          chargersToRemove -= remove;
        });
        var updateKeys = Object.keys(updates);
        if (updateKeys.length === 1) return withGift({ type: 'change', key: updateKeys[0], quantity: updates[updateKeys[0]] });
        if (updateKeys.length > 1) return withGift({ type: 'update', updates: updates });

        if (!paid) continue;
        var itemsToAdd = [];
        var hasFirstCard = group.cards.some(function (item) { return (item.properties || {})._bundle_free_slot !== '2'; });
        var hasSecondCard = group.cards.some(function (item) { return (item.properties || {})._bundle_free_slot === '2'; });
        for (var slot = 1; slot <= targetCards; slot++) {
          if ((slot === 1 && hasFirstCard) || (slot === 2 && hasSecondCard)) continue;
          var variantId = configuredFreeVariant(paid, slot);
          if (!variantId) continue;
          itemsToAdd.push({
            id: Number(variantId), quantity: 1, parent_line_key: paid.key,
            properties: {
              _bundle_offer: (paid.properties || {})._bundle_offer,
              _bundle_free: 'true', _bundle_free_slot: String(slot), _bundle_instance: group.instance
            }
          });
        }
        if (targetCharger && !chargerCount) {
          var chargerVariantId = configuredChargerVariant(paid);
          if (chargerVariantId) itemsToAdd.push({
            id: Number(chargerVariantId), quantity: 1, parent_line_key: paid.key,
            properties: {
              _bundle_offer: (paid.properties || {})._bundle_offer,
              _bundle_free: 'true', _bundle_free_type: 'charger', _bundle_instance: group.instance
            }
          });
        }
        if (itemsToAdd.length) return withGift({ type: 'add', items: itemsToAdd });
      }
      return withGift(null);
    }

    function cartFingerprint(cart) {
      return JSON.stringify((cart.items || []).map(function (item) { return [item.key, item.quantity]; }));
    }

    async function syncRewards(cart, renderReady) {
      if (!cart || !Array.isArray(cart.items)) cart = await getCart();
      rememberCart(cart);
      for (var pass = 0; pass < 8; pass++) {
        var action = nextRewardAction(cart);
        if (!action) return;
        var before = cartFingerprint(cart);
        if (action.type === 'change') {
          cart = await changeFreeLine(action.key, action.quantity, renderReady);
        } else if (action.type === 'update') {
          cart = await updateFreeLines(action.updates, renderReady);
        } else {
          var added = await addFreeLines(action.items, cart, renderReady);
          if (giftAddBlocked && !added.length && action.items.every(function (item) { return (item.properties || {})._bundle_free_type === 'mystery_gift'; })) return;
          if (added.length !== action.items.length || added.some(function (item) { return !item.key; })) {
            cart = await getCart();
          } else {
            cart = {
              items: cart.items.concat(added),
              item_count: (typeof cart.item_count === 'number' ? cart.item_count : cart.items.reduce(function (sum, line) { return sum + line.quantity; }, 0)) + action.items.reduce(function (sum, item) { return sum + item.quantity; }, 0)
            };
          }
        }
        rememberCart(cart);
        if (cartFingerprint(cart) === before) throw new Error('The cart did not apply the bundle reward change.');
      }
    }

    // A bundle quantity click can update the paid line and its rewards in a
    // single cart request. Decreases use update.js with line keys; increases
    // add the paid difference and newly earned children in one add.js batch.
    async function changeBundleQuantityFast(paid, quantity) {
      var currentItems = cartItems;
      var instance = (paid.properties || {})._bundle_instance;
      var proposedItems = currentItems.map(function (item) {
        if (item.key !== paid.key) return item;
        return Object.assign({}, item, { quantity: quantity });
      });
      var rewardAction = nextRewardAction({ items: proposedItems });
      var beforeCount = currentItems.reduce(function (sum, item) { return sum + item.quantity; }, 0);

      if (quantity < paid.quantity) {
        var updates = Object.create(null);
        updates[paid.key] = quantity;
        if (rewardAction && rewardAction.type === 'change') updates[rewardAction.key] = rewardAction.quantity;
        else if (rewardAction && rewardAction.type === 'update') Object.assign(updates, rewardAction.updates);
        else if (rewardAction && rewardAction.type === 'add') throw new Error('Bundle rewards need repair before a fast decrease.');
        var updatedCart = await updateFreeLines(updates, Promise.resolve());
        rememberCart(updatedCart);
        var updatedPaid = updatedCart.items.find(function (item) { return (item.properties || {})._bundle_instance === instance && (item.properties || {})._bundle_free !== 'true'; });
        if (quantity > 0 && (!updatedPaid || updatedPaid.quantity !== quantity)) throw new Error('The combined cart update did not set the paid quantity.');
        if (nextRewardAction(updatedCart)) await syncRewards(updatedCart, Promise.resolve());
        return;
      }

      if (quantity > paid.quantity) {
        if (rewardAction && rewardAction.type !== 'add') throw new Error('Bundle rewards need repair before a fast increase.');
        var items = [{ id: Number(paid.variant_id), quantity: quantity - paid.quantity, properties: paid.properties }];
        if (rewardAction) rewardAction.items.forEach(function (item) {
          var child = Object.assign({}, item, { parent_id: Number(paid.variant_id) });
          delete child.parent_line_key;
          items.push(child);
        });
        var added = await addFreeLines(items, { items: currentItems, item_count: beforeCount }, Promise.resolve(), 'cart');
        var paidResult = added.find(function (item) {
          var properties = item.properties || {};
          return properties._bundle_instance === instance && properties._bundle_free !== 'true';
        });
        if (!paidResult || !paidResult.key || paidResult.quantity !== quantity || added.length !== items.length) {
          var freshCart = await getCart();
          rememberCart(freshCart);
          var freshPaid = freshCart.items.find(function (item) {
            var properties = item.properties || {};
            return properties._bundle_instance === instance && properties._bundle_free !== 'true';
          });
          if (!freshPaid || freshPaid.quantity !== quantity) throw new Error('The combined cart add did not update the paid quantity.');
          await syncRewards(freshCart, Promise.resolve());
          return;
        }
        var nextItems = currentItems.map(function (item) {
          return item.key === paid.key ? Object.assign({}, item, paidResult) : item;
        });
        added.forEach(function (item) {
          if (item.key !== paidResult.key) nextItems.push(item);
        });
        rememberCart({ items: nextItems });
      }
    }

    function legacyBundle1Identity(paid) {
      var properties = paid.properties || {};
      if (properties._bundle_instance || properties._bundle_offer || properties._bundle_free === 'true' || paid.selling_plan_allocation || (paid.parent_relationship && paid.parent_relationship.parent)) return null;
      var root = document.querySelector('.spotmystuff-hero-purchase-v3');
      var cta = root && root.querySelector('.spotmystuff-hero-purchase-v3__cta--1');
      var variant = cta && cta.querySelector('form[data-type="add-to-cart-form"] input[name="id"]');
      if (!variant || String(variant.value) !== String(paid.variant_id)) return null;
      if (bundle1LegacyInstances[paid.key]) return bundle1LegacyInstances[paid.key];
      var identity = bundle1CartIdentity(root);
      if (!identity) return null;
      return bundle1LegacyInstances[paid.key] = Object.assign({}, properties, identity);
    }

    // Handle only our paid bundle rows. Other cart controls keep Horizon's
    // normal behavior. If a combined request fails, restore that behavior.
    document.addEventListener('quantity-selector:update', function (event) {
      if (!cartItems || !event.detail || !event.detail.cartLine || !event.target || !event.target.closest) return;
      var component = event.target.closest('cart-items-component');
      var row = event.target.closest('.cart-items__table-row');
      if (!component || !row || typeof component.updateQuantity !== 'function') return;
      var paid = cartItems.find(function (item) { return item.key === row.dataset.key; });
      if (!paid || (paid.properties || {})._bundle_free === 'true') return;
      var registration = !(paid.properties || {})._bundle_instance ? legacyBundle1Identity(paid) : null;
      if (!registration && (!(paid.properties || {})._bundle_instance || ((paid.properties || {})._bundle_offer !== 'bundle_2' && (paid.properties || {})._bundle_offer !== 'bundle_3'))) return;
      if (!Number.isInteger(event.detail.quantity) || event.detail.quantity < 0) return;
      if (registration && event.detail.quantity === 0) return;
      event.stopPropagation();
      var line = event.detail.cartLine;
      var quantity = event.detail.quantity;
      var instance = registration ? registration._bundle_instance : (paid.properties || {})._bundle_instance;
      component._smst3PendingFast = (component._smst3PendingFast || 0) + 1;
      if (component.classList) component.classList.add('cart-items-disabled');
      queue = queue.then(async function () {
        giftAddBlocked = false;
        var latestPaid = cartItems && cartItems.find(function (item) {
          var properties = item.properties || {};
          return properties._bundle_instance === instance && properties._bundle_free !== 'true';
        });
        if (!latestPaid && registration && quantity > 0) {
          var legacy = cartItems && cartItems.find(function (item) { return item.key === paid.key; });
          if (legacy) {
            var registeredCart = await changeFreeLine(legacy.key, legacy.quantity, Promise.resolve(), registration);
            rememberCart(registeredCart);
            latestPaid = registeredCart.items.find(function (item) {
              return (item.properties || {})._bundle_instance === instance && (item.properties || {})._bundle_free !== 'true';
            });
          }
        }
        if (!latestPaid || latestPaid.quantity === quantity) return;
        return changeBundleQuantityFast(latestPaid, quantity);
      })
        .catch(function (error) {
          if (window.console && console.warn) console.warn('[spotmystuff-hero-purchase-v3] Fast cart update failed; retrying with Horizon:', error);
          return getCart().then(function (cart) {
            rememberCart(cart);
            var currentPaid = cart.items.find(function (item) { return ((item.properties || {})._bundle_instance === instance || (registration && item.key === paid.key)) && (item.properties || {})._bundle_free !== 'true'; });
            if (!currentPaid || currentPaid.quantity === quantity) return syncRewards(cart, Promise.resolve());
            var currentLine = cart.items.findIndex(function (item) { return item.key === currentPaid.key; }) + 1 || line;
            return component.updateQuantity({ line: currentLine, quantity: quantity, action: quantity === 0 ? 'clear' : 'change' });
          });
        })
        .catch(function (error) {
          if (window.console && console.error) console.error('[spotmystuff-hero-purchase-v3] Cart update could not be recovered:', error);
        })
        .finally(function () {
          component._smst3PendingFast--;
          if (!component._smst3PendingFast && component.classList) component.classList.remove('cart-items-disabled');
        });
    }, true);

    document.addEventListener('shopify:cart:lines-update', function (event) {
      if (event.action !== 'update' && event.action !== 'remove') return;
      if (event.context !== 'cart' || !event.promise) return;
      // Only respond to Horizon's quantity control, never our own event.
      if (!event.target || !event.target.closest || !event.target.closest('cart-items-component')) return;
      var component = event.target.closest('cart-items-component');
      queue = queue.then(function () { return event.promise; })
        .then(function (result) {
          if (result && result.detail && result.detail.didError) return;
          giftAddBlocked = false;
          var cart = result && result.detail && Array.isArray(result.detail.items)
            ? { items: result.detail.items, item_count: result.detail.itemCount }
            : null;
          return syncRewards(cart, waitForNativeCartRender(component));
        })
        .catch(function (error) {
          if (window.console && console.error) console.error('[spotmystuff-hero-purchase-v3] Bundle cart sync failed:', error);
        });
    });
  }

  // Keep each free cart line at its included quantity. Bundle 3 has two
  // separate free-card lines of 1 each. Both bundles may use the same free
  // variant, so lock each rendered line's own quantity.
  function initBundleFreeQuantityLock() {
    if (window.__smst3BundleFreeQuantityLockReady) return;
    window.__smst3BundleFreeQuantityLockReady = true;

    function lockFreeQuantity() {
      var freeVariantIds = [];
      var freeCardVariantIds = [];
      var freeChargerVariantIds = [];
      var freeGiftVariantIds = [];
      document.querySelectorAll('.spotmystuff-hero-purchase-v3 [data-bundle2-free-variant-id]').forEach(function (cta) {
        var id = cta.getAttribute('data-bundle2-free-variant-id');
        if (id) {
          freeVariantIds.push(id);
          freeCardVariantIds.push(id);
        }
        var chargerId = cta.getAttribute('data-bundle2-free-charger-variant-id');
        if (chargerId) {
          freeVariantIds.push(chargerId);
          freeChargerVariantIds.push(chargerId);
        }
        var giftId = cta.getAttribute('data-bundle2-mystery-gift-variant-id');
        if (giftId) {
          freeVariantIds.push(giftId);
          freeGiftVariantIds.push(giftId);
        }
      });
      document.querySelectorAll('.spotmystuff-hero-purchase-v3 [data-bundle3-free-variant-id]').forEach(function (cta) {
        var id = cta.getAttribute('data-bundle3-free-variant-id');
        if (id) {
          freeVariantIds.push(id);
          freeCardVariantIds.push(id);
        }
        var secondId = cta.getAttribute('data-bundle3-free-variant-id-2');
        if (secondId) {
          freeVariantIds.push(secondId);
          freeCardVariantIds.push(secondId);
        }
        var chargerId = cta.getAttribute('data-bundle3-free-charger-variant-id');
        if (chargerId) {
          freeVariantIds.push(chargerId);
          freeChargerVariantIds.push(chargerId);
        }
        var giftId = cta.getAttribute('data-bundle3-mystery-gift-variant-id');
        if (giftId) {
          freeVariantIds.push(giftId);
          freeGiftVariantIds.push(giftId);
        }
      });
      if (!freeVariantIds.length) return;

      document.querySelectorAll('cart-quantity-selector-component[data-variant-id]').forEach(function (selector) {
        var variantId = selector.getAttribute('data-variant-id');
        if (freeVariantIds.indexOf(variantId) === -1) return;
        var input = selector.querySelector('input[name="updates[]"]');
        if (!input) return;
        input.min = input.value;
        input.max = input.value;
        input.readOnly = true;
        input.setAttribute('aria-readonly', 'true');
        if (typeof selector.updateButtonStates === 'function') {
          selector.updateButtonStates();
        } else {
          var plus = selector.querySelector('button[name="plus"]');
          var minus = selector.querySelector('button[name="minus"]');
          if (plus) plus.disabled = true;
          if (minus) minus.disabled = true;
        }

        var row = selector.closest('.cart-items__table-row');
        if (freeGiftVariantIds.indexOf(variantId) !== -1) {
          addCartOfferTag(row, 'smst3-cart-free-gift-tag', 'FREE GIFT');
        } else if (freeChargerVariantIds.indexOf(variantId) !== -1) {
          addCartOfferTag(row, 'smst3-cart-free-charger-tag', 'FREE CHARGER');
        } else if (freeCardVariantIds.indexOf(variantId) !== -1) {
          addCartOfferTag(row, 'smst3-cart-free-card-tag', 'FREE CARD');
        }
      });
    }

    lockFreeQuantity();
    new MutationObserver(lockFreeQuantity).observe(document.body, { childList: true, subtree: true });
  }

  // Change: 3-up bundle selector — shows only the selected bundle's own
  // native buy-buttons panel and updates the matching reward badge.
  // Price, checked state, and card highlight are plain HTML radios +
  // CSS. Bundle 2's Add to Cart
  // form has its own handler above; the other panels retain Horizon's
  // native buy-button behavior.
  function initChargerAddon(root) {
    var addon = root.querySelector('[data-smst3-charger-addon]');
    if (!addon) return;
    var panels = addon.querySelectorAll('[data-smst3-charger-panel]');
    var toggle = addon.querySelector('[data-smst3-charger-toggle]');
    function update() {
      var checked = root.querySelector('[data-spotmystuff-v3-cardgroup] .spotmystuff-hero-purchase-v3__card-radio:checked');
      var match = checked && checked.id.match(/spotmystuff-v3-bundle-(\d+)-/);
      var index = match ? match[1] : '1';
      var visible = false;
      panels.forEach(function (panel) {
        panel.hidden = panel.getAttribute('data-smst3-charger-panel') !== index;
        if (!panel.hidden) visible = true;
      });
      addon.hidden = !visible;
      addon.dataset.selected = toggle && toggle.checked && !toggle.disabled ? 'true' : 'false';
    }
    if (!addon._smst3ChargerReady) {
      addon._smst3ChargerReady = true;
      root.addEventListener('change', function (event) {
        if (event.target === toggle || event.target.classList.contains('spotmystuff-hero-purchase-v3__card-radio')) update();
      });
    }
    update();
  }

  function initCardSwitcher(root) {
    initBundleButtonPrices(root);
    var radios = root.querySelectorAll('[data-spotmystuff-v3-cardgroup] .spotmystuff-hero-purchase-v3__card-radio');
    if (!radios.length) return;

    var ctas = root.querySelectorAll('[data-spotmystuff-v3-cta]');
    var progress = root.querySelector('[data-spotmystuff-v3-progress]');
    // Marks every panel as JS-controlled so the CSS no-JS fallback (which
    // forces Bundle 1's panel visible when JS hasn't run) steps aside
    // and lets .cta--active alone decide what's shown from here on.
    ctas.forEach(function (cta) {
      cta.classList.add('spotmystuff-hero-purchase-v3__cta--js-ready');
    });

    function applySelection() {
      var checked = root.querySelector('[data-spotmystuff-v3-cardgroup] .spotmystuff-hero-purchase-v3__card-radio:checked');
      if (!checked) return;
      // Radio ids are "spotmystuff-v3-bundle-{n}-{section.id}" — pull n
      // out rather than hardcoding three separate ids, so this keeps
      // working if the section is ever extended to more/fewer bundles.
      var match = checked.id.match(/spotmystuff-v3-bundle-(\d+)-/);
      var index = match ? match[1] : '1';
      if (progress) progress.dataset.bundle = index;
      ctas.forEach(function (cta) {
        var isMatch = cta.classList.contains('spotmystuff-hero-purchase-v3__cta--' + index);
        cta.classList.toggle('spotmystuff-hero-purchase-v3__cta--active', isMatch);
      });
    }

    radios.forEach(function (radio) {
      radio.addEventListener('change', applySelection);
    });
    applySelection();
  }

  // Reuse each bundle's Shopify-formatted price without changing its native button.
  function initBundleButtonPrices(root) {
    if (root._smst3ButtonPricesCleanup) return;
    function update() {
      var radios = Array.from(root.querySelectorAll('[data-spotmystuff-v3-cardgroup] .spotmystuff-hero-purchase-v3__card-radio'));
      root.querySelectorAll('[data-spotmystuff-v3-cta]').forEach(function (cta) {
        var match = cta.className.match(/spotmystuff-hero-purchase-v3__cta--(\d+)(?:\s|$)/);
        if (!match) return;
        var radio = radios.find(function (input) { return input.id.indexOf('spotmystuff-v3-bundle-' + match[1] + '-') === 0; });
        var card = radio && radio.nextElementSibling;
        var source = card && card.querySelector('.spotmystuff-hero-purchase-v3__card-price:not(.spotmystuff-hero-purchase-v3__card-price--empty)');
        var price = source && source.textContent.trim();
        cta.querySelectorAll('button.add-to-cart-button').forEach(function (button) {
          var suffix = button.querySelector('[data-smst3-button-price]');
          var content = button.querySelector('.add-to-cart-text__content');
          if (!price || !content || button.disabled) {
            if (suffix) suffix.remove();
            return;
          }
          if (!suffix) {
            suffix = document.createElement('span');
            suffix.setAttribute('data-smst3-button-price', '');
            (content.querySelector('span > span') || content).appendChild(suffix);
          }
          var text = '\u00a0\u00a0\u00a0' + price;
          if (suffix.textContent !== text) suffix.textContent = text;
        });
      });
    }
    // Horizon can replace a button when a variant changes; restore only the price suffix.
    var observer = new MutationObserver(update);
    root.querySelectorAll('[data-spotmystuff-v3-cardgroup], [data-spotmystuff-v3-cta]').forEach(function (node) {
      observer.observe(node, { childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ['disabled'] });
    });
    function cleanup() {
      observer.disconnect();
      document.removeEventListener('shopify:section:unload', onUnload);
      delete root._smst3ButtonPricesCleanup;
    }
    function onUnload(event) {
      if (event.target === root || event.target.contains(root)) cleanup();
    }
    root._smst3ButtonPricesCleanup = cleanup;
    document.addEventListener('shopify:section:unload', onUnload);
    update();
  }

  // This theme's real scroll box isn't <html>/<body> (both are
  // `overflow: hidden`) but a custom `.page-wrapper` div, so the browser's
  // native "jump to #hash" behavior — which only ever scrolls the
  // document — silently does nothing here.
  //
  // Native `scrollIntoView({behavior:'smooth'})` and `scroll-behavior:
  // smooth` both get cut short partway on this theme — confirmed live —
  // by something in its own navigation/transition handling, landing well
  // short of the target. Driving the scroll manually frame-by-frame with
  // rAF sidesteps whatever cancels the browser's own smooth-scroll, so it
  // reliably animates all the way to the target instead of jumping there
  // instantly.
  function findScrollableAncestor(el) {
    var node = el.parentElement;
    while (node && node !== document.body) {
      var style = getComputedStyle(node);
      if (
        (style.overflowY === 'auto' || style.overflowY === 'scroll') &&
        node.scrollHeight > node.clientHeight
      ) {
        return node;
      }
      node = node.parentElement;
    }
    return document.scrollingElement || document.documentElement;
  }

  function smoothScrollTo(target, duration) {
    var scroller = findScrollableAncestor(target);
    var scrollerRect = scroller.getBoundingClientRect();
    var targetRect = target.getBoundingClientRect();
    var startY = scroller.scrollTop;
    var deltaY = targetRect.top - scrollerRect.top;
    var startTime = null;

    function easeInOutQuad(t) {
      return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
    }

    function step(timestamp) {
      if (startTime === null) startTime = timestamp;
      var elapsed = timestamp - startTime;
      var progress = Math.min(elapsed / duration, 1);
      scroller.scrollTop = startY + deltaY * easeInOutQuad(progress);
      if (progress < 1) {
        window.requestAnimationFrame(step);
      }
    }
    window.requestAnimationFrame(step);
  }

  function initInPageAnchors(root) {
    root.querySelectorAll('a[href^="#"]').forEach(function (link) {
      link.addEventListener('click', function (event) {
        var id = link.getAttribute('href').slice(1);
        if (!id) return;
        var target = document.getElementById(id);
        if (!target) return;
        event.preventDefault();
        smoothScrollTo(target, 600);
        if (window.history && window.history.pushState) {
          window.history.pushState(null, '', '#' + id);
        }
      });
    });
  }

  function initJudgeMePreview(root) {
    var slot = root.querySelector('[data-hero-judgeme]');
    if (!slot || slot._smst3JudgeMeReady) return;
    slot._smst3JudgeMeReady = true;
    var productId = slot.getAttribute('data-product-id');
    var placementObserver;

    function syncRating(badge) {
      var summary = badge.querySelector('.jdgm-prev-badge');
      var stars = badge.querySelector('.jdgm-prev-badge__stars');
      if (!summary || !stars) return;
      stars.setAttribute('tabindex', '-1');
      var averageValue = summary.getAttribute('data-average-rating');
      var average = Number(averageValue);
      var count = Number(summary.getAttribute('data-number-of-reviews'));
      var rating = summary.querySelector('[data-hero-judgeme-rating]');
      if (averageValue === null || averageValue === '' || !Number.isFinite(average) || average < 0 || average > 5 || !Number.isFinite(count) || count <= 0) {
        if (rating) rating.remove();
        return;
      }
      // Hero display override only; Judge.me's stored review data stays unchanged.
      var reviewText = summary.querySelector('.jdgm-prev-badge__text');
      if (reviewText && reviewText.textContent !== '5232 reviews') {
        reviewText.textContent = '5232 reviews';
      }
      if (!rating) {
        rating = document.createElement('span');
        rating.setAttribute('data-hero-judgeme-rating', '');
        stars.insertAdjacentElement('afterend', rating);
      }
      var text = average + '/5';
      if (rating.textContent !== text) rating.textContent = text;
    }

    function placeBadge() {
      if (!root.isConnected) {
        if (placementObserver) placementObserver.disconnect();
        return;
      }
      var badge = Array.from(document.querySelectorAll('.jdgm-widget.jdgm-preview-badge')).find(function (candidate) {
        var owner = candidate.closest('[data-hero-judgeme]');
        return candidate.getAttribute('data-id') === productId && (!owner || owner === slot);
      });
      if (!badge) return;
      slot.appendChild(badge);
      slot.hidden = false;
      if (placementObserver) placementObserver.disconnect();
      syncRating(badge);
      new MutationObserver(function () { syncRating(badge); }).observe(badge, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['data-average-rating', 'data-number-of-reviews']
      });
    }

    function openReviews(event) {
      if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      var target = document.getElementById('judgeme_product_reviews');
      if (!target || (target.getAttribute('data-id') || target.getAttribute('data-product-id')) !== productId) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        target.scrollIntoView({ behavior: 'instant', block: 'start' });
      } else {
        smoothScrollTo(target, 600);
      }
    }
    slot.addEventListener('click', openReviews, true);
    slot.addEventListener('keydown', openReviews, true);
    placementObserver = new MutationObserver(placeBadge);
    placementObserver.observe(document.body, { childList: true, subtree: true });
    placeBadge();
  }

  function initCouponCopy(root) {
    root.querySelectorAll('[data-coupon-copy]').forEach(function (button) {
      if (button._smst3CouponCopyReady) return;
      button._smst3CouponCopyReady = true;
      var label = button.querySelector('[data-coupon-copy-label]');
      var badge = button.closest('.spotmystuff-hero-purchase-v3__coupon-badge');
      var feedback = badge && badge.querySelector('[data-coupon-feedback]');
      var resetTimer;
      var copying = false;

      function copyWithSelection(code) {
        var activeElement = document.activeElement;
        var field = document.createElement('textarea');
        field.value = code;
        field.readOnly = true;
        field.style.cssText = 'position:fixed;left:0;top:0;width:1px;height:1px;opacity:0;font-size:16px;';
        document.body.appendChild(field);
        try {
          field.focus({ preventScroll: true });
          field.select();
          field.setSelectionRange(0, code.length);
          if (!document.execCommand('copy')) throw new Error('Clipboard copy was declined.');
        } finally {
          field.remove();
          if (activeElement && typeof activeElement.focus === 'function') activeElement.focus({ preventScroll: true });
        }
      }

      button.addEventListener('click', async function (event) {
        event.preventDefault();
        if (copying) return;
        copying = true;
        var code = button.getAttribute('data-coupon-copy');
        clearTimeout(resetTimer);
        if (feedback) { feedback.textContent = ''; feedback.removeAttribute('data-error'); }
        try {
          if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
            try { await navigator.clipboard.writeText(code); }
            catch (error) { copyWithSelection(code); }
          } else copyWithSelection(code);
          button.setAttribute('data-copied', 'true');
          button.setAttribute('aria-label', code + ' copied to clipboard');
          if (label) label.textContent = 'Copied!';
          if (feedback) feedback.textContent = code + ' copied. Paste it at checkout.';
        } catch (error) {
          button.removeAttribute('data-copied');
          if (label) label.textContent = code;
          if (feedback) {
            feedback.setAttribute('data-error', 'true');
            feedback.textContent = 'Copy was blocked. Use code ' + code + ' at checkout.';
          }
        } finally {
          copying = false;
          resetTimer = setTimeout(function () {
            button.removeAttribute('data-copied');
            button.setAttribute('aria-label', 'Copy discount code ' + code);
            if (label) label.textContent = code;
          }, 2000);
        }
      });
    });
  }

  function initHeaderOverlap(root) {
    if (root._smst3HeaderOverlapCleanup) return;
    var headerGroup = document.getElementById('header-group');
    var section = root.closest('.shopify-section');
    var main = section && section.closest('main');
    if (!headerGroup || !main || !headerGroup.contains(document.querySelector('.spotmystuff-header-section'))) return;
    if (!(headerGroup.compareDocumentPosition(section) & Node.DOCUMENT_POSITION_FOLLOWING)) return;
    // Never pull a Hero over another content section above it.
    var firstSection = Array.from(main.children).find(function (child) {
      return child.classList.contains('shopify-section') && child.getClientRects().length && child.offsetHeight > 0;
    });
    if (firstSection !== section) return;

    function dimensions(element) {
      var style = getComputedStyle(element);
      if (style.display === 'none') return { flow: 0, cover: 0 };
      // Sticky headers use display:contents on #header-group. That wrapper has
      // no box, but its header and announcement still occupy document flow.
      if (style.display === 'contents') {
        return Array.from(element.children).reduce(function (sum, child) {
          var size = dimensions(child);
          return { flow: sum.flow + size.flow, cover: sum.cover + size.cover };
        }, { flow: 0, cover: 0 });
      }
      var height = element.getBoundingClientRect().height;
      var outer = height + (parseFloat(style.marginTop) || 0) + (parseFloat(style.marginBottom) || 0);
      var inFlow = style.position !== 'fixed' && style.position !== 'absolute';
      var cover = height === 0 && style.visibility !== 'hidden' && (!style.clipPath || style.clipPath === 'none') && !/hidden|clip/.test(style.overflowY) ? Array.from(element.children).reduce(function (sum, child) {
        return sum + dimensions(child).cover;
      }, 0) : outer;
      return { flow: inFlow ? Math.max(0, outer) : 0, cover: Math.max(0, cover) };
    }
    function update() {
      // Matching margin and padding offsets cancel in document flow. Keep them
      // stable while the announcement animates, so the whole Hero follows its
      // layout immediately without per-frame style writes or a second tween.
      if (headerGroup.querySelector('.sms-announcement[data-scroll-moving]')) return;
      var size = dimensions(headerGroup);
      var flow = size.flow + 'px';
      var stack = Math.max(size.flow, size.cover) + 'px';
      if (section.style.getPropertyValue('--smst3-header-flow-height') !== flow) section.style.setProperty('--smst3-header-flow-height', flow);
      if (section.style.getPropertyValue('--smst3-header-stack-height') !== stack) section.style.setProperty('--smst3-header-stack-height', stack);
    }
    update();
    headerGroup.setAttribute('data-smst3-hero-header', '');
    section.setAttribute('data-smst3-header-overlap', '');
    var observer;
    var mutations;
    var observed = new Set();
    function observeSections() {
      if (!observer) return;
      var targets = new Set([headerGroup].concat(Array.from(headerGroup.children)));
      observed.forEach(function (element) { if (!targets.has(element)) observer.unobserve(element); });
      targets.forEach(function (element) { if (!observed.has(element)) observer.observe(element); });
      observed = targets;
    }
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(update);
      observeSections();
    }
    if (typeof MutationObserver !== 'undefined') {
      mutations = new MutationObserver(function () { observeSections(); update(); });
      mutations.observe(headerGroup, { childList: true, subtree: true, attributes: true, attributeFilter: ['sticky', 'hidden', 'data-scroll-moving'] });
    }
    window.addEventListener('resize', update);
    function unload(event) {
      if (event.target !== section) return;
      if (observer) observer.disconnect();
      if (mutations) mutations.disconnect();
      window.removeEventListener('resize', update);
      document.removeEventListener('shopify:section:unload', unload);
      headerGroup.removeAttribute('data-smst3-hero-header');
      section.removeAttribute('data-smst3-header-overlap');
      section.style.removeProperty('--smst3-header-flow-height');
      section.style.removeProperty('--smst3-header-stack-height');
      delete root._smst3HeaderOverlapCleanup;
    }
    root._smst3HeaderOverlapCleanup = unload;
    document.addEventListener('shopify:section:unload', unload);
  }

  function initSingleBundleShippingPill(root) {
    if (root._smst3ShippingPillReady) return;
    var single = root.querySelector('.spotmystuff-hero-purchase-v3__card--single .spotmystuff-hero-purchase-v3__card-shippill');
    var reference = root.querySelector('.spotmystuff-hero-purchase-v3__card--popular .spotmystuff-hero-purchase-v3__card-shippill');
    if (!single || !reference) return;
    root._smst3ShippingPillReady = true;
    var appearanceProperties = [
      'box-sizing', 'padding', 'border', 'border-radius', 'background-color',
      'box-shadow', 'font-family', 'font-size', 'font-weight', 'font-style',
      'line-height', 'letter-spacing', 'word-spacing', 'text-transform', 'color',
      'align-items', 'justify-content', 'gap'
    ];
    function copyAppearance(target, source) {
      var style = window.getComputedStyle(source);
      appearanceProperties.forEach(function (property) {
        target.style.setProperty(property, style.getPropertyValue(property));
      });
    }
    function matchSize() {
      copyAppearance(single, reference);
      [
        '.spotmystuff-hero-purchase-v3__card-shipicon',
        '.spotmystuff-hero-purchase-v3__card-ship-badge',
        '.spotmystuff-hero-purchase-v3__card-ship-text',
        '.spotmystuff-hero-purchase-v3__card-ship-text strong'
      ].forEach(function (selector) {
        var target = single.querySelector(selector);
        var source = reference.querySelector(selector);
        if (target && source) copyAppearance(target, source);
      });
      // Copy layout dimensions so a scaled theme preview does not shrink the pill twice.
      var size = window.getComputedStyle(reference);
      if (parseFloat(size.width) > 0 && parseFloat(size.height) > 0) {
        single.style.setProperty('--smst3-single-shipping-width', size.width);
        single.style.setProperty('--smst3-single-shipping-height', size.height);
      } else {
        single.style.removeProperty('--smst3-single-shipping-width');
        single.style.removeProperty('--smst3-single-shipping-height');
      }
    }
    matchSize();
    if (typeof ResizeObserver !== 'undefined') new ResizeObserver(matchSize).observe(reference);
    else window.addEventListener('resize', matchSize);
  }

  function initDeliveryNote(root) {
    var note = root.querySelector('[data-smst3-delivery-note]');
    if (!note || note._smst3DeliveryCleanup) return;
    var date = note.querySelector('[data-smst3-delivery-date]');
    var prefix = note.querySelector('[data-smst3-delivery-prefix]');
    if (!date || !prefix) return;
    var timer;
    var stopped = false;
    function update() {
      if (stopped) return;
      if (!note.isConnected) { cleanup(); return; }
      var today = new Date();
      // Calendar days in the visitor's local date, including month/year and DST changes.
      var delivery = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 3, 12);
      prefix.textContent = 'by ';
      date.textContent = delivery.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
      date.setAttribute('datetime', [delivery.getFullYear(), String(delivery.getMonth() + 1).padStart(2, '0'), String(delivery.getDate()).padStart(2, '0')].join('-'));
      var midnight = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
      window.clearTimeout(timer);
      timer = window.setTimeout(update, Math.max(1, midnight.getTime() - Date.now() + 50));
    }
    function onVisibility() { if (document.visibilityState === 'visible') update(); }
    function onUnload(event) { if (event.target === root || (event.target.contains && event.target.contains(root))) cleanup(); }
    function cleanup() {
      stopped = true;
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
      document.removeEventListener('shopify:section:unload', onUnload);
      window.removeEventListener('pageshow', update);
      delete note._smst3DeliveryCleanup;
    }
    note._smst3DeliveryCleanup = cleanup;
    document.addEventListener('visibilitychange', onVisibility);
    document.addEventListener('shopify:section:unload', onUnload);
    window.addEventListener('pageshow', update);
    update();
  }

  function init() {
    var sections = document.querySelectorAll('.spotmystuff-hero-purchase-v3');
    sections.forEach(function (root) {
      initHeaderOverlap(root);
      initCta(root);
      initCardSwitcher(root);
      initChargerAddon(root);
      initInPageAnchors(root);
      initJudgeMePreview(root);
      initCouponCopy(root);
      initSingleBundleShippingPill(root);
      initDeliveryNote(root);
    });
    if (sections.length) {
      initBundleFreeQuantityLock();
      initBundleCartQuantitySync();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', function () {
    document.querySelectorAll('.spotmystuff-hero-purchase-v3').forEach(function (root) {
      initHeaderOverlap(root);
      initSingleBundleShippingPill(root);
      initDeliveryNote(root);
    });
  });
})();


/* Customer video carousel */
/* Gesture-driven finite carousel. No automatic scrolling, repeat playback, or native controls. */
(() => {
  if (customElements.get('spotmystuff-ugc-carousel')) return;

  class SpotMyStuffUGCCarousel extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.track = this.querySelector('[data-ugc-track]');
      this.rail = this.querySelector('[data-ugc-rail]');
      if (!this.track || !this.rail) { this.abort = null; return; }
      this.rail.querySelectorAll('[data-ugc-clone]').forEach(card => card.remove());
      this.originals = [...this.rail.querySelectorAll('[data-ugc-card]')];
      if (!this.originals.length) { this.abort = null; return; }
      this.position = 0;
      this.pitch = 0;
      this.drag = null;
      this.suppressClickUntil = 0;
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

      this.cards = this.originals;
      this.videos = this.cards.map(card => card.querySelector('video'));
      this.videos.forEach(video => this.prepareVideo(video, signal));

      this.addEventListener('click', event => {
        if (performance.now() < this.suppressClickUntil) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        const card = event.target.closest('[data-ugc-card]');
        if (!card || !this.contains(card)) return;
        event.preventDefault();
        this.toggleVideo(card.querySelector('video'));
      }, { signal, capture: true });

      this.track.addEventListener('pointerdown', event => {
        if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;
        this.stopMotion();
        this.suppressClickUntil = 0;
        this.drag = {
          id: event.pointerId, x: event.clientX, y: event.clientY,
          lastX: event.clientX, time: performance.now(), moved: false, velocity: 0
        };
      }, { signal });
      document.addEventListener('pointermove', event => this.dragMove(event), { signal, passive: false });
      document.addEventListener('pointerup', event => this.dragEnd(event, false), { signal });
      document.addEventListener('pointercancel', event => this.dragEnd(event, true), { signal });
      this.track.addEventListener('dragstart', event => event.preventDefault(), { signal });
      this.track.addEventListener('wheel', event => {
        const horizontal = Math.abs(event.deltaX) > Math.abs(event.deltaY);
        if (!horizontal && !event.shiftKey) return;
        event.preventDefault();
        this.stopMotion();
        this.pauseForSwipe();
        const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? this.viewportWidth : 1;
        this.position += (horizontal ? event.deltaX : event.deltaY) * unit;
        this.render();
      }, { signal, passive: false });
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        this.stopMotion();
        this.pauseForSwipe();
        const delta = (event.key === 'ArrowRight' ? 1 : -1) * this.pitch;
        this.animateStep(delta);
      }, { signal });

      this.setAttribute('data-ugc-ready', '');
      this.track.scrollLeft = 0;
      this.measure();
      this.resizeObserver = new ResizeObserver(() => this.measure());
      this.resizeObserver.observe(this.track);
      this.visibilityObserver = new IntersectionObserver(entries => {
        if (!entries[0].isIntersecting) {
          this.stopMotion();
          this.videos.forEach(video => this.pauseVideo(video));
        }
      });
      this.visibilityObserver.observe(this);
    }

    prepareVideo(video, signal) {
      const card = video.closest('[data-ugc-card]');
      const button = card.querySelector('[data-ugc-play]');
      video._ugcLabel = button.getAttribute('aria-label').replace(/^Pause /, 'Play ');
      const poster = card.querySelector('.spotmystuff-hero-purchase-v3__ugc-poster');
      if (poster) video.poster = poster.currentSrc || poster.src;
      video._ugcToken = 0;
      video._ugcFinished = video._ugcFinished || video.ended;
      video._ugcPauseRequested = false;
      video.controls = false;
      video.removeAttribute('controls');
      video.loop = false;
      video.removeAttribute('loop');
      video.preload = 'metadata';
      video.disablePictureInPicture = true;
      video.disableRemotePlayback = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.tabIndex = -1;
      card.classList.remove('is-playing', 'is-starting', 'has-frame', 'has-paused-frame');
      video._ugcPauseCanvas = card.querySelector('[data-ugc-paused-frame]');
      if (video._ugcPauseCanvas) video._ugcPauseCanvas.hidden = true;
      button.hidden = false;
      button.setAttribute('aria-label', video._ugcLabel);
      video.addEventListener('loadedmetadata', () => {
        if (card.classList.contains('is-starting')) this.restorePosition(video);
      }, { signal });
      video.addEventListener('playing', () => {
        this.trackPresentedFrames(video);
        this.restorePosition(video);
        this.revealFrame(video);
      }, { signal });
      video.addEventListener('seeked', () => {
        if (!video.paused && card.classList.contains('is-starting')) this.revealFrame(video);
      }, { signal });
      video.addEventListener('pause', () => {
        const requested = video._ugcPauseRequested;
        video._ugcPauseRequested = false;
        this.capturePausedFrame(video);
        if (!requested) {
          this.rememberPosition(video);
          this.alignPausedFrame(video);
        }
        this.resetButton(video);
      }, { signal });
      video.addEventListener('ended', () => {
        video._ugcFinished = true;
        video._ugcPresentedTime = null;
        video._ugcResumeTime = null;
        video._ugcResumeTarget = null;
        this.resetButton(video);
      }, { signal });
      video.addEventListener('error', () => {
        card.classList.remove('has-frame');
        this.clearPausedFrame(video);
        this.resetButton(video);
      }, { signal });
    }

    toggleVideo(video) {
      this.stopMotion();
      const card = video.closest('[data-ugc-card]');
      if (!video.paused || card.classList.contains('is-starting')) {
        this.pauseVideo(video);
        return;
      }
      this.videos.forEach(other => { if (other !== video) this.pauseVideo(other); });
      if (video.ended || video._ugcFinished) {
        video._ugcFinished = false;
        video._ugcPresentedTime = null;
        video._ugcResumeTime = null;
        this.clearPausedFrame(video);
        card.classList.remove('has-frame');
        video.currentTime = 0;
      }
      video._ugcResumeTarget = video._ugcResumeTime;
      this.restorePosition(video);
      // Uncover the existing paused video before its media clock starts. Waiting
      // for playing/frame events can hide forward playback on slower phones.
      if (card.classList.contains('has-frame') && video.readyState >= 2 && !video.seeking) {
        this.clearPausedFrame(video);
      }
      card.classList.add('is-starting');
      card.querySelector('[data-ugc-play]').setAttribute('aria-label', 'Cancel video loading');
      const token = ++video._ugcToken;
      video.play().catch(() => {
        if (token === video._ugcToken) this.resetButton(video);
      });
    }

    revealFrame(video) {
      const resuming = video.closest('[data-ugc-card]').classList.contains('has-frame');
      const token = video._ugcToken;
      const valid = () => this.abort && !video.paused && token === video._ugcToken;
      const show = () => {
        if (!valid() || video.seeking || video.readyState < 2 || !video.videoWidth || !video.videoHeight) return;
        const resumeTime = video._ugcResumeTarget;
        if (Number.isFinite(resumeTime) && resumeTime > 0 && video.currentTime < resumeTime - .12) {
          this.restorePosition(video);
          this.revealFrame(video);
          return;
        }
        const card = video.closest('[data-ugc-card]');
        video._ugcResumeTarget = null;
        card.classList.remove('is-starting');
        card.classList.add('has-frame', 'is-playing');
        // A paused clip already has a painted video surface: uncover its first
        // resumed frame immediately, rather than playing behind a fading still.
        if (resuming) this.clearPausedFrame(video);
        else card.classList.remove('has-paused-frame');
        card.querySelector('[data-ugc-play]').setAttribute('aria-label', video._ugcLabel.replace(/^Play /, 'Pause '));
      };
      const afterPaint = (_now, metadata) => {
        video._ugcFrame = null;
        if (!valid()) return;
        const resumeTime = video._ugcResumeTarget;
        // Mobile decoders may present an earlier keyframe while a resume seek
        // settles. Keep the paused cover until the presented frame catches up.
        if (Number.isFinite(resumeTime) && resumeTime > 0 && Number.isFinite(metadata?.mediaTime) && metadata.mediaTime < resumeTime - .12) {
          this.restorePosition(video);
          video._ugcFrame = video.requestVideoFrameCallback(afterPaint);
          return;
        }
        if (resuming) {
          show();
          return;
        }
        // A decoded frame callback runs before composition. Keep the poster
        // covering that frame for two paints, then crossfade it out.
        video._ugcPaint1 = requestAnimationFrame(() => {
          video._ugcPaint2 = requestAnimationFrame(show);
        });
      };
      if (video._ugcFrame != null) video.cancelVideoFrameCallback?.(video._ugcFrame);
      cancelAnimationFrame(video._ugcPaint1);
      cancelAnimationFrame(video._ugcPaint2);
      if (video.requestVideoFrameCallback) {
        video._ugcFrame = video.requestVideoFrameCallback(afterPaint);
      } else {
        afterPaint();
      }
    }

    resetButton(video) {
      if (video._ugcTrackFrame != null) video.cancelVideoFrameCallback?.(video._ugcTrackFrame);
      video._ugcTrackFrame = null;
      ++video._ugcToken;
      if (video._ugcFrame != null) video.cancelVideoFrameCallback?.(video._ugcFrame);
      cancelAnimationFrame(video._ugcPaint1);
      cancelAnimationFrame(video._ugcPaint2);
      const card = video.closest('[data-ugc-card]');
      card.classList.remove('is-playing', 'is-starting');
      card.querySelector('[data-ugc-play]').setAttribute('aria-label', video._ugcLabel);
    }

    capturePausedFrame(video) {
      const card = video.closest('[data-ugc-card]');
      if (!this.isConnected || !card.classList.contains('has-frame') || video.readyState < 2 || !video.videoWidth || !video.videoHeight) return;
      if (video.paused && card.classList.contains('has-paused-frame')) return;
      let canvas = video._ugcPauseCanvas;
      if (!canvas) {
        canvas = document.createElement('canvas');
        canvas.className = 'spotmystuff-hero-purchase-v3__ugc-paused-frame';
        canvas.setAttribute('data-ugc-paused-frame', '');
        canvas.setAttribute('aria-hidden', 'true');
        card.insertBefore(canvas, card.querySelector('[data-ugc-play]'));
        video._ugcPauseCanvas = canvas;
      }
      try {
        const scale = Math.min(1, 1280 / Math.max(video.videoWidth, video.videoHeight));
        const width = Math.max(1, Math.round(video.videoWidth * scale));
        const height = Math.max(1, Math.round(video.videoHeight * scale));
        if (canvas.width !== width) canvas.width = width;
        if (canvas.height !== height) canvas.height = height;
        const context = canvas.getContext('2d');
        if (!context) return;
        // Display only: no pixel readback/export, so cross-origin Shopify media
        // can still provide a temporary cover for its paused video surface.
        context.drawImage(video, 0, 0, width, height);
        canvas.hidden = false;
        card.classList.add('has-paused-frame');
      } catch (_) {
        this.clearPausedFrame(video);
      }
    }

    clearPausedFrame(video) {
      video.closest('[data-ugc-card]').classList.remove('has-paused-frame');
      if (video._ugcPauseCanvas) video._ugcPauseCanvas.hidden = true;
    }

    trackPresentedFrames(video) {
      if (!video.requestVideoFrameCallback || video._ugcTrackFrame != null) return;
      const next = (_now, metadata) => {
        video._ugcTrackFrame = null;
        if (!this.abort || video.paused) return;
        if (Number.isFinite(metadata.mediaTime)) video._ugcPresentedTime = metadata.mediaTime;
        video._ugcTrackFrame = video.requestVideoFrameCallback(next);
      };
      video._ugcTrackFrame = video.requestVideoFrameCallback(next);
    }

    rememberPosition(video) {
      if (!video.ended && !video.seeking && Number.isFinite(video.currentTime) && video.currentTime > 0) {
        const displayed = video._ugcPresentedTime;
        const age = video.currentTime - displayed;
        // The media clock can lead the picture by a few frames on mobile.
        // Save a fresh presented frame; use native time if unavailable or stale.
        video._ugcResumeTime = Number.isFinite(displayed) && age >= 0 && age <= .15 ? displayed : video.currentTime;
      }
    }

    alignPausedFrame(video) {
      const time = video._ugcResumeTime;
      if (!video.paused || video.ended || video.seeking || video.readyState < 1 || !Number.isFinite(time)) return;
      if (Math.abs(video.currentTime - time) > .005) {
        // Align while paused and covered, never seek during forward playback.
        try { video.currentTime = time; } catch (_) { /* Keep native resume available. */ }
      }
    }

    restorePosition(video) {
      const time = video._ugcResumeTarget;
      if (!Number.isFinite(time) || time <= 0 || video.readyState < 1) return;
      // A delayed playing event can arrive after normal forward progress.
      // Seek only if the timeline actually fell behind the pause point.
      if (!video.seeking && video.currentTime < time - .08) {
        try { video.currentTime = time; } catch (_) { /* Retry after metadata is ready. */ }
      }
    }

    pauseVideo(video) {
      if (!video.paused) {
        // Stop the media clock before drawing a potentially expensive mobile
        // canvas snapshot, so the still and saved pause point cannot drift.
        video._ugcPauseRequested = true;
        video.pause();
      }
      this.capturePausedFrame(video);
      this.rememberPosition(video);
      this.alignPausedFrame(video);
      this.resetButton(video);
    }

    pauseForSwipe() {
      this.videos.forEach(video => {
        this.pauseVideo(video);
        // Return to the poster when browsing between cards.
        video.closest('[data-ugc-card]').classList.remove('has-frame');
        this.clearPausedFrame(video);
      });
    }

    dragMove(event) {
      const drag = this.drag;
      if (!drag || event.pointerId !== drag.id) return;
      const dx = event.clientX - drag.x;
      const dy = event.clientY - drag.y;
      if (!drag.moved) {
        if (Math.abs(dx) < 7) return;
        if (Math.abs(dy) > Math.abs(dx)) { this.drag = null; return; }
        drag.moved = true;
        this.pauseForSwipe();
        this.track.classList.add('is-dragging');
        this.track.setPointerCapture?.(event.pointerId);
      }
      event.preventDefault();
      const now = performance.now();
      const delta = drag.lastX - event.clientX;
      const elapsed = Math.max(8, now - drag.time);
      drag.velocity = Math.max(-2.5, Math.min(2.5, drag.velocity * .35 + delta / elapsed * .65));
      this.position += delta;
      drag.lastX = event.clientX;
      drag.time = now;
      this.render();
    }

    dragEnd(event, cancelled) {
      const drag = this.drag;
      if (!drag || drag.id !== event.pointerId) return;
      this.drag = null;
      this.track.classList.remove('is-dragging');
      if (this.track.hasPointerCapture?.(event.pointerId)) this.track.releasePointerCapture(event.pointerId);
      if (!drag.moved) return;
      this.suppressClickUntil = performance.now() + 350;
      if (!cancelled && performance.now() - drag.time < 100 && !this.reducedMotion.matches) this.coast(drag.velocity);
    }

    measure() {
      const phase = this.pitch ? this.position / this.pitch : 0;
      this.viewportWidth = this.track.clientWidth;
      const gap = parseFloat(getComputedStyle(this.rail).columnGap) || 0;
      this.cardWidth = this.originals[0].getBoundingClientRect().width;
      this.pitch = this.cardWidth + gap;
      this.maxPosition = Math.max(0, this.pitch * this.originals.length - gap - this.viewportWidth);
      this.position = phase * this.pitch;
      this.render();
    }

    render() {
      if (!this.pitch) return;
      const offset = Math.max(0, Math.min(this.position, this.maxPosition));
      this.position = offset;
      this.rail.style.transform = `translate3d(${-offset}px, 0, 0)`;
      this.cards.forEach((card, index) => {
        const left = index * this.pitch - offset;
        const visible = left + this.cardWidth > 1 && left < this.viewportWidth - 1;
        if (card._ugcVisible === visible) return;
        card._ugcVisible = visible;
        card.setAttribute('aria-hidden', String(!visible));
        card.querySelector('[data-ugc-play]').tabIndex = visible ? 0 : -1;
        if (!visible) this.pauseVideo(card.querySelector('video'));
      });
    }

    coast(velocity) {
      this.stopMotion();
      let previous = performance.now();
      const step = time => {
        const elapsed = Math.min(32, Math.max(0, time - previous));
        previous = time;
        this.position += velocity * elapsed;
        velocity *= Math.exp(-elapsed / 180);
        this.render();
        if (Math.abs(velocity) > .015 && this.abort) this.motion = requestAnimationFrame(step);
        else this.motion = null;
      };
      if (Math.abs(velocity) > .015) this.motion = requestAnimationFrame(step);
    }

    animateStep(delta) {
      const start = this.position;
      if (this.reducedMotion.matches) { this.position += delta; this.render(); return; }
      const began = performance.now();
      const step = time => {
        const progress = Math.min(1, Math.max(0, (time - began) / 180));
        this.position = start + delta * (1 - (1 - progress) ** 3);
        this.render();
        if (progress < 1 && this.abort) this.motion = requestAnimationFrame(step);
        else this.motion = null;
      };
      this.motion = requestAnimationFrame(step);
    }

    stopMotion() {
      cancelAnimationFrame(this.motion);
      this.motion = null;
    }

    disconnectedCallback() {
      this.stopMotion();
      this.videos?.forEach(video => this.pauseVideo(video));
      this.abort?.abort();
      this.abort = null;
      this.resizeObserver?.disconnect();
      this.visibilityObserver?.disconnect();
      this.rail?.style.removeProperty('transform');
      this.track?.classList.remove('is-dragging');
      this.removeAttribute('data-ugc-ready');
    }
  }
  customElements.define('spotmystuff-ugc-carousel', SpotMyStuffUGCCarousel);
})();

/* Desktop left-column scroll follow. The gallery and FAQ travel together so
   the shorter column does not leave a blank area beside the purchase panel. */
(() => {
  if (window.__smst3LeftFollowLoaded) return;
  window.__smst3LeftFollowLoaded = true;
  const selector = '.spotmystuff-hero-purchase-v3';
  const desktop = matchMedia('(min-width: 901px)');
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

  function scrollContainer(element) {
    for (let node = element.parentElement; node && node !== document.body; node = node.parentElement) {
      const overflow = getComputedStyle(node).overflowY;
      if ((overflow === 'auto' || overflow === 'scroll') && node.scrollHeight > node.clientHeight + 1) return node;
    }
    return document.scrollingElement || document.documentElement;
  }

  function initialize(section) {
    if (section._smst3LeftFollowCleanup) return;
    const inner = section.querySelector('.spotmystuff-hero-purchase-v3__inner');
    const extra = section.querySelector('.spotmystuff-hero-purchase-v3__gallery-extra');
    const gallery = section.querySelector('.spotmystuff-hero-purchase-v3__gallery');
    const panel = section.querySelector('.spotmystuff-hero-purchase-v3__panel');
    const faq = extra?.querySelector('.spotmystuff-hero-purchase-v3__faq');
    if (!inner || !extra || !gallery || !panel) return;

    const scroller = scrollContainer(section);
    const pageScroll = scroller === document.scrollingElement || scroller === document.documentElement;
    const scrollTarget = pageScroll ? window : scroller;
    const controller = new AbortController();
    const signal = controller.signal;
    let frame = 0;
    let current = 0;
    let closedLeftBottom = null;
    let reservedBottom = 0;

    // Remember the collapsed accordion's position before any answer grows.
    // Its new height must not pull the whole left column upward.
    faq?.addEventListener('click', event => {
      if (!event.target.closest('summary') || faq.querySelector('details[open]')) return;
      closedLeftBottom = extra.getBoundingClientRect().bottom - inner.getBoundingClientRect().top - current;
    }, { capture: true, signal });

    function update() {
      frame = 0;
      if (!section.isConnected) { section._smst3LeftFollowCleanup(); return; }
      let next = 0;
      if (desktop.matches && !reducedMotion.matches) {
        const innerRect = inner.getBoundingClientRect();
        // Subtract the current transform so the travel limit remains stable.
        const measuredLeftBottom = extra.getBoundingClientRect().bottom - innerRect.top - current;
        const answerOpen = !!faq?.querySelector('details[open]');
        if (!answerOpen) closedLeftBottom = measuredLeftBottom;
        const leftBottom = answerOpen ? (closedLeftBottom ?? measuredLeftBottom) : measuredLeftBottom;
        const panelBottom = panel.getBoundingClientRect().bottom - innerRect.top;
        const maxTravel = Math.max(0, Math.floor(panelBottom - leftBottom));
        const viewportTop = pageScroll ? 0 : scroller.getBoundingClientRect().top + scroller.clientTop;
        const header = parseFloat(getComputedStyle(section).getPropertyValue('--header-height')) || 24;
        next = Math.min(maxTravel, Math.max(0, Math.floor(header - (innerRect.top - viewportTop))));
      }
      if (next !== current) {
        current = next;
        if (next) inner.style.setProperty('--smst3-left-follow', `${next}px`);
        else inner.style.removeProperty('--smst3-left-follow');
      }
      // The translated accordion can extend below the grid's normal height
      // while an answer is open. Reserve that space in document flow so the
      // next Shopify section begins after the entire answer.
      const innerRect = inner.getBoundingClientRect();
      const visualLeftBottom = extra.getBoundingClientRect().bottom;
      const naturalSectionBottom = innerRect.bottom - reservedBottom;
      const neededBottom = desktop.matches ? Math.max(0, Math.ceil(visualLeftBottom - naturalSectionBottom)) : 0;
      if (neededBottom !== reservedBottom) {
        reservedBottom = neededBottom;
        if (reservedBottom) inner.style.paddingBottom = `${reservedBottom}px`;
        else inner.style.removeProperty('padding-bottom');
      }
    }

    function schedule() {
      if (!frame) frame = requestAnimationFrame(update);
    }

    scrollTarget.addEventListener('scroll', schedule, { passive: true, signal });
    window.addEventListener('resize', schedule, { passive: true, signal });
    desktop.addEventListener('change', schedule, { signal });
    reducedMotion.addEventListener('change', schedule, { signal });
    const resizeObserver = new ResizeObserver(schedule);
    [section, inner, gallery, extra, panel].forEach(node => resizeObserver.observe(node));
    section._smst3LeftFollowCleanup = () => {
      controller.abort();
      resizeObserver.disconnect();
      cancelAnimationFrame(frame);
      inner.style.removeProperty('--smst3-left-follow');
      inner.style.removeProperty('padding-bottom');
      section._smst3LeftFollowCleanup = null;
    };
    schedule();
  }

  function initializeWithin(root) {
    if (root.matches?.(selector)) initialize(root);
    root.querySelectorAll?.(selector).forEach(initialize);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initializeWithin(document), { once: true });
  else initializeWithin(document);
  document.addEventListener('shopify:section:load', event => initializeWithin(event.target));
  document.addEventListener('shopify:section:unload', event => {
    if (event.target.matches?.(selector)) event.target._smst3LeftFollowCleanup?.();
    event.target.querySelectorAll?.(selector).forEach(section => section._smst3LeftFollowCleanup?.());
  });
})();

/* Animate the native FAQ details downward while retaining keyboard support. */
(() => {
  if (window.__smst3FaqMotionLoaded) return;
  window.__smst3FaqMotionLoaded = true;
  const selector = '.spotmystuff-hero-purchase-v3__faq';
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

  function initialize(faq) {
    if (faq._smst3FaqMotionCleanup) return;
    const controller = new AbortController();
    faq.addEventListener('click', event => {
      const summary = event.target.closest('summary');
      const details = summary?.parentElement;
      if (!details?.matches('.spotmystuff-hero-purchase-v3__faq-item') || !faq.contains(details)) return;
      event.preventDefault();

      const opening = details.classList.contains('is-closing') || !details.open;
      const startHeight = details.getBoundingClientRect().height;
      details._smst3FaqAnimation?.cancel();
      details._smst3FaqAnimation = null;
      details.classList.toggle('is-closing', !opening);
      if (opening) details.open = true;

      if (reducedMotion.matches || typeof details.animate !== 'function') {
        details.open = opening;
        details.classList.remove('is-closing');
        details.style.removeProperty('overflow');
        return;
      }

      const style = getComputedStyle(details);
      const borders = (parseFloat(style.borderTopWidth) || 0) + (parseFloat(style.borderBottomWidth) || 0);
      const closedHeight = summary.getBoundingClientRect().height + borders;
      const openHeight = details.scrollHeight + borders;
      details.style.overflow = 'hidden';
      const animation = details.animate([
        { height: `${startHeight}px` },
        { height: `${opening ? openHeight : closedHeight}px` }
      ], { duration: opening ? 340 : 280, easing: 'cubic-bezier(.22, 1, .36, 1)' });
      details._smst3FaqAnimation = animation;
      animation.addEventListener('finish', () => {
        if (details._smst3FaqAnimation !== animation) return;
        if (!opening) details.open = false;
        details.style.removeProperty('overflow');
        details.classList.remove('is-closing');
        details._smst3FaqAnimation = null;
      }, { once: true });
    }, { signal: controller.signal });
    faq._smst3FaqMotionCleanup = () => {
      controller.abort();
      faq.querySelectorAll('.spotmystuff-hero-purchase-v3__faq-item').forEach(details => {
        details._smst3FaqAnimation?.cancel();
        details.style.removeProperty('overflow');
        details.classList.remove('is-closing');
      });
      faq._smst3FaqMotionCleanup = null;
    };
  }

  function initializeWithin(root) {
    if (root.matches?.(selector)) initialize(root);
    root.querySelectorAll?.(selector).forEach(initialize);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initializeWithin(document), { once: true });
  else initializeWithin(document);
  document.addEventListener('shopify:section:load', event => initializeWithin(event.target));
  document.addEventListener('shopify:section:unload', event => {
    if (event.target.matches?.(selector)) event.target._smst3FaqMotionCleanup?.();
    event.target.querySelectorAll?.(selector).forEach(faq => faq._smst3FaqMotionCleanup?.());
  });
})();
